import * as React from "react";

import "./ComponentExamplesContent.scss";

import { getParameters } from "codesandbox-import-utils/lib/api/define";

import { IExample } from "./Examples.Types";
import ThemeToggleButton from "./ThemeToggleButton";

import { getPackageVersion } from "../configuration";

import { Breakpoint } from "azure-devops-ui/Breakpoint";
import { Button } from "azure-devops-ui/Button";
import { announce } from "azure-devops-ui/Core/Util/Accessibility";
import { equals } from "azure-devops-ui/Core/Util/String";
import { Dropdown, DropdownExpandableButton, IDropdownExpandableProps } from "azure-devops-ui/Dropdown";
import { ListSelection } from "azure-devops-ui/List";
import { IListBoxItem } from "azure-devops-ui/ListBox";
import { SurfaceBackground, SurfaceContext } from "azure-devops-ui/Surface";
import { css } from "azure-devops-ui/Util";
import { DropdownSelection } from "azure-devops-ui/Utilities/DropdownSelection";

import { ObservableValue } from "azure-devops-ui/Core/Observable";
import { Observer } from "azure-devops-ui/Observer";
import PrismTsx from "react-syntax-highlighter/dist/esm/languages/prism/tsx";
import { default as SyntaxHighlighter } from "react-syntax-highlighter/dist/esm/prism-light";
import PrismStyle from "react-syntax-highlighter/dist/esm/styles/prism/tomorrow";

import fluentFilledFont from "azure-devops-ui/Components/Icon/fonts/fluent-filled-v1.1.201.woff2";
import fluentRegularFont from "azure-devops-ui/Components/Icon/fonts/fluent-regular-v1.1.201.woff2";

SyntaxHighlighter.registerLanguage("tsx", PrismTsx);

export interface IComponentExamplesContentProps {
    /**
     * Set of examples to display.
     */
    examples: IExample[];
}

export interface IComponentExamplesContentState {
    selectedExample: IExample;
    showCode?: boolean;
}

function getShowCodeOption(): boolean {
    let showCode = false;
    try {
        showCode = window.localStorage.getItem("show-code") === "true";
    } catch (ex) {
        // Could not read from local storage
    }
    return showCode;
}

function setShowCodeOption(showCode: boolean): void {
    try {
        window.localStorage.setItem("show-code", showCode ? "true" : "false");
    } catch (ex) {
        // Could not read from local storage
    }
}

export default class ComponentExamplesContent extends React.Component<IComponentExamplesContentProps, IComponentExamplesContentState> {
    private sandboxFormElement = React.createRef<HTMLFormElement>();
    private selection: DropdownSelection;
    private items: Array<IListBoxItem<IExample>>;
    private iconOnly = new ObservableValue<boolean>(false);

    constructor(props: IComponentExamplesContentProps) {
        super(props);

        this.items = props.examples.map(e => {
            return { id: this.getExampleId(e), text: e.title, data: e };
        });

        this.selection = new DropdownSelection();
        let selectedIndex = 0;
        let example: IExample | undefined;
        if (!example) {
            if (window.location.hash) {
                example = this.getExampleById(window.location.hash.substr(1));
                selectedIndex = this.items.findIndex(listBoxItem => listBoxItem.data === example);
            }
            if (!example) {
                example = props.examples[0];
            }
        }

        this.selection.select(selectedIndex >= 0 ? selectedIndex : 0);

        this.state = {
            selectedExample: example,
            showCode: getShowCodeOption()
        };
    }

    public render(): JSX.Element | null {
        const { examples } = this.props;
        const { selectedExample, showCode } = this.state;

        if (!examples || !examples.length) {
            return null;
        }

        return (
            <Observer iconOnly={this.iconOnly}>
                {(props: { iconOnly: boolean }) => (
                    <div className="component-examples flex-grow flex-column">
                        <Breakpoint breakpoints={[1, 750]} onBreakpoint={this.onBreakpointChange} />
                        <div className="component-examples-header flex-row flex-center flex-wrap">
                            <Dropdown
                                ariaLabel={"Example picker " + selectedExample.title + " selected"}
                                className="component-example-picker"
                                selection={this.selection}
                                onSelect={this.onSelect}
                                items={this.items}
                                renderExpandable={this.renderDropdownExpandable}
                            />
                            <div className="component-example-buttons flex-row flex-wrap">
                                {selectedExample.code && this.renderOpenInCodeSandboxButton(selectedExample, props.iconOnly)}
                                <ThemeToggleButton iconOnly={props.iconOnly} />
                                {selectedExample.code && (
                                    <Button
                                        iconProps={{ iconName: "Embed" }}
                                        text={props.iconOnly ? undefined : showCode ? "Hide code" : "Show code"}
                                        tooltipProps={props.iconOnly ? { text: showCode ? "Hide code" : "Show code" } : undefined}
                                        onClick={this.onShowCodeClick}
                                    />
                                )}
                            </div>
                        </div>
                        <div className="component-example-body">
                            <div className={css("component-example-surface themed", selectedExample.normalBackground && "bolt-page-white")}>
                                <SurfaceContext.Provider value={{ background: SurfaceBackground.neutral }}>
                                    <div className="flex-row justify-center">
                                        <div
                                            className={css(
                                                "component-example-content flex-grow flex-column",
                                                !selectedExample.stretchContainer && "flex-center"
                                            )}
                                        >
                                            {React.createElement(selectedExample.component, {})}
                                        </div>
                                    </div>
                                </SurfaceContext.Provider>
                            </div>
                        </div>
                        {selectedExample.code && showCode && (
                            <div className="component-example-source flex-grow">
                                <SyntaxHighlighter language="tsx" style={PrismStyle}>
                                    {selectedExample.code}
                                </SyntaxHighlighter>
                            </div>
                        )}
                    </div>
                )}
            </Observer>
        );
    }

    private onBreakpointChange = (index: number, size: number) => {
        this.iconOnly.value = index === 0;
    };

    private renderDropdownExpandable = (props: IDropdownExpandableProps) => {
        return <DropdownExpandableButton {...props} renderSelectedItems={this.renderSelectedItems} />;
    };

    private getExampleId(example: IExample): string {
        return example.title
            .toLocaleLowerCase()
            .replace(/-/g, "_")
            .replace(/ /g, "-");
    }

    private getExampleById(exampleName: string): IExample {
        const nameToMatch = exampleName.replace(/-/g, " ").replace(/_/g, "-");
        return this.props.examples.filter(e => equals(e.title, nameToMatch, true))[0];
    }

    private onSelect = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<IExample>) => {
        const selectedExample = item.data!;
        if (selectedExample !== this.state.selectedExample) {
            window.location.hash = this.getExampleId(selectedExample);
            this.setState({ selectedExample });
        }
    };

    private onShowCodeClick = (): void => {
        const newShowCodeState = !this.state.showCode;
        setShowCodeOption(newShowCodeState);
        this.setState({ showCode: newShowCodeState });
        if (newShowCodeState) {
            announce("The code is shown");
        } else {
            announce("The code is hidden");
        }
    };

    private renderOpenInCodeSandboxButton(example: IExample, iconOnly: boolean): JSX.Element {
        const params = {
            files: {
                "example.tsx": {
                    content: example.code!,
                    isBinary: false
                },
                "iconFont.css": {
                    content: `@font-face {
                                font-family: "FluentFilled";
                                src: url(${fluentFilledFont}) format("woff2");
                            }
                            
                            @font-face {
                                font-family: "FluentRegular";
                                src: url(${fluentRegularFont}) format("woff2");
                            }`,
                    isBinary: false
                },
                "index.html": {
                    content: `<div id="root" style="padding: 20px; background-color: rgb(248, 248, 248); position: absolute; top: 0; right: 0; bottom: 0; left: 0"></div>`,
                    isBinary: false
                },
                "index.tsx": {
                    content: `import React from "react";
import ReactDOM from "react-dom";
import Example from "./example";
import "./iconFont.css";

import { SurfaceBackground, SurfaceContext } from "azure-devops-ui/Surface";

ReactDOM.render(
    <SurfaceContext.Provider value={{ background: SurfaceBackground.neutral }}>
      <Example />
    </SurfaceContext.Provider>,
    document.getElementById("root")
);`,
                    isBinary: false
                },
                "package.json": {
                    content: JSON.stringify(
                        {
                            dependencies: {
                                "azure-devops-ui": getPackageVersion(),
                                react: "16.8.1",
                                "react-dom": "16.8.1",
                                "react-scripts": "2.0.3"
                            },
                            main: "index.tsx"
                        },
                        null,
                        4
                    ),
                    isBinary: false
                }
            }
        };

        if (example.dependencies) {
            Object.keys(example.dependencies).forEach(depName => {
                params.files[depName] = {
                    content: example.dependencies![depName],
                    isBinary: false
                };
            });
        }

        return (
            <form action="https://codesandbox.io/api/v1/sandboxes/define" method="POST" ref={this.sandboxFormElement} target="_blank">
                <input type="hidden" name="parameters" value={getParameters(params)} />
                <input type="hidden" name="query" value="module=example.tsx" />
                <Button
                    text={iconOnly ? undefined : "Open in sandbox"}
                    tooltipProps={iconOnly ? { text: "Open in sandbox" } : undefined}
                    onClick={this.openInSandboxButtonClicked}
                    iconProps={{
                        render: () => <img alt="" className="custom-button-icon hc-invert-dark" src={require("../Images/codesandbox.svg")} />
                    }}
                    role="link"
                />
            </form>
        );
    }

    private openInSandboxButtonClicked = (): void => {
        if (this.sandboxFormElement.current) {
            this.sandboxFormElement.current.submit();
        }
    };

    // Remove this once the Dropdown component is fixed.
    private renderSelectedItems = (selection: ListSelection, items: Array<IListBoxItem<IExample>>) => {
        return <span className="flex-row flex-grow">{items[selection.value[0].beginIndex].text}</span>;
    };
}
